import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, Card, Field } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

type Step = 'qr' | 'confirm' | 'done';

export default function TotpSetupPage() {
  const { me, reload } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('qr');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!me) return;
    if (me.role === 'client') {
      nav('/', { replace: true });
      return;
    }
    if (step !== 'qr') return;
    api<{ otpauthUri: string; secretBase32: string }>('/api/auth/2fa/setup', { body: {} })
      .then((r) => {
        setOtpauthUri(r.otpauthUri);
        setSecret(r.secretBase32);
      })
      .catch((e: ApiError) => setError(e.message));
  }, [me, step]);

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await api<{ backupCodes: string[] }>('/api/auth/2fa/confirm', { body: { code } });
      setBackupCodes(r.backupCodes);
      setStep('done');
      await reload();
    } catch (e: any) {
      setError(e?.message ?? 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  // QR rendered via a public chart helper that takes the otpauth URI as query.
  // We use chart.googleapis as a fallback-free QR renderer (no extra dep).
  const qrUrl = otpauthUri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(otpauthUri)}`
    : '';

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div style={{ maxWidth: 480, width: '100%' }}>
        <div className="mono" style={{ fontSize: 11, letterSpacing: '0.18em', color: 'var(--accent)' }}>SEGURIDAD · 2FA</div>
        <h1 className="display" style={{ fontSize: 24, fontWeight: 600, margin: '8px 0 16px' }}>
          {step === 'done' ? '¡Listo! 2FA activado' : 'Configura 2FA en tu app'}
        </h1>

        {step === 'qr' && otpauthUri && (
          <Card>
            <p style={{ color: 'var(--text-2)', fontSize: 14, marginTop: 0 }}>
              Escanea el QR con Google Authenticator, Authy, 1Password o Microsoft Authenticator. Si no puedes escanear, copia el secreto manualmente.
            </p>
            <div style={{ display: 'grid', placeItems: 'center', padding: 14, background: 'white', borderRadius: 12, margin: '12px 0' }}>
              <img src={qrUrl} alt="QR 2FA" width={220} height={220} />
            </div>
            <div className="mono" style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.08em', marginTop: 8 }}>SECRETO</div>
            <div className="mono" style={{ fontSize: 13, padding: 10, background: 'var(--surface-2)', borderRadius: 8, wordBreak: 'break-all' }}>
              {secret}
            </div>
            <div style={{ marginTop: 18, textAlign: 'right' }}>
              <Btn onClick={() => setStep('confirm')}>Ya lo añadí, continuar</Btn>
            </div>
          </Card>
        )}

        {step === 'confirm' && (
          <Card>
            <form onSubmit={confirm}>
              <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '0 0 14px' }}>
                Escribe el código de 6 dígitos que muestra tu app ahora mismo.
              </p>
              <Field label="Código TOTP" value={code} onChange={setCode} placeholder="123456" />
              {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 10 }}>{error}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                <Btn variant="ghost" onClick={() => setStep('qr')}>Volver al QR</Btn>
                <Btn type="submit" disabled={loading || code.length !== 6}>
                  {loading ? 'Validando…' : 'Activar 2FA'}
                </Btn>
              </div>
            </form>
          </Card>
        )}

        {step === 'done' && (
          <Card>
            <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '0 0 14px' }}>
              Tu 2FA está activo. <strong style={{ color: 'var(--text)' }}>Guarda estos 8 códigos de respaldo</strong> en un lugar seguro — cada uno sirve UNA sola vez si pierdes el acceso a tu app.
            </p>
            <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-2)', borderRadius: 10, padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontFamily: 'JetBrains Mono', fontSize: 14, letterSpacing: '0.08em' }}>
              {backupCodes.map((c) => (
                <div key={c}>{c}</div>
              ))}
            </div>
            <p style={{ color: 'var(--text-3)', fontSize: 11, marginTop: 10 }}>
              Después de cerrar esta página los códigos NO se pueden ver de nuevo. Si los pierdes, tendrás que pedir un reset a otro admin.
            </p>
            <div style={{ marginTop: 18, textAlign: 'right' }}>
              <Btn onClick={() => nav('/', { replace: true })}>Continuar al backoffice</Btn>
            </div>
          </Card>
        )}

        {error && step === 'qr' && (
          <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 12 }}>{error}</div>
        )}
      </div>
    </div>
  );
}
